import React, { useState, useEffect } from "react";

interface CollaboratorOption {
  id: string;
  name: string;
}

interface MoveActionFormProps {
  onSubmit: (value: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  label?: string;
  placeholder?: string;
  showCollaborateToggle?: boolean;
  isCollaborating?: boolean;
  onToggleCollaborate?: (value: boolean) => void;
  showCollaboratorSelect?: boolean;
  collaboratorOptions?: CollaboratorOption[];
  selectedCollaborators?: string[];
  onCollaboratorsChange?: (values: string[]) => void;
  showMovementRules?: boolean;
  showScanRules?: boolean;
  showAgencyScanRules?: boolean;
  currentLocation?: string | null;
  isSpaceshipScan?: boolean;
  isSpaceshipMove?: boolean;
  isAgencyScan?: boolean;
  scanLevel?: number | null;
}

export default function MoveActionForm({ 
  onSubmit, 
  onCancel, 
  isSubmitting = false,
  label = "Route",
  placeholder = "Enter route",
  showCollaborateToggle = false,
  isCollaborating = false,
  onToggleCollaborate,
  showCollaboratorSelect = false,
  collaboratorOptions = [],
  selectedCollaborators = [],
  onCollaboratorsChange,
  showMovementRules = false,
  showScanRules = false,
  showAgencyScanRules = false,
  currentLocation = null,
  isSpaceshipScan = false,
  isSpaceshipMove = false,
  isAgencyScan = false,
  scanLevel = null,
}: MoveActionFormProps) {
  const [value, setValue] = useState<string>("");
  const [value2, setValue2] = useState<string>("");
  const [value3, setValue3] = useState<string>("");
  const [value4, setValue4] = useState<string>("");
  const [value5, setValue5] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Determine how many inputs to show based on collaborator count or scan level
  const collaboratorCount = selectedCollaborators.length;
  const inputCount = isAgencyScan
    ? (scanLevel ?? 1) // For agency scan, number of inputs = scan level
    : isCollaborating 
      ? (isSpaceshipScan 
          ? (collaboratorCount === 1 ? 3 : collaboratorCount >= 2 ? 5 : 1)
          : isSpaceshipMove
          ? (collaboratorCount === 1 ? 2 : collaboratorCount >= 2 ? 3 : 1)
          : 1)
      : 1;

  // Reset additional inputs when collaborators change or collaboration is toggled off
  useEffect(() => {
    if (!isCollaborating || (!isSpaceshipScan && !isSpaceshipMove && !isAgencyScan)) {
      setValue2("");
      setValue3("");
      setValue4("");
      setValue5("");
    }
  }, [isCollaborating, isSpaceshipScan, isSpaceshipMove, isAgencyScan, selectedCollaborators.length]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSpaceshipMove && isCollaborating) {
      // For spaceship move with collaboration, combine all inputs with right arrows (only non-empty values)
      const values = [value, value2, value3, value4, value5].slice(0, inputCount);
      const nonEmptyValues = values.filter(v => v.trim());
      
      // Check if at least one value is provided
      if (nonEmptyValues.length === 0) {
        setError(`${label} is required`);
        return;
      }
      
      // Combine current location with input values
      const details = nonEmptyValues.join(" → ");
      const allValues = currentLocation 
        ? `Current location: ${currentLocation}\n${details}`
        : details;
      
      onSubmit(allValues);
    } else if (isSpaceshipScan && isCollaborating) {
      // For spaceship scan with collaboration, combine all inputs in order (only non-empty values)
      const values = [value, value2, value3, value4, value5].slice(0, inputCount);
      const nonEmptyValues = values.filter(v => v.trim());
      
      // Check if at least one value is provided
      if (nonEmptyValues.length === 0) {
        setError(`${label} is required`);
        return;
      }
      
      // Combine current location with input values
      const details = nonEmptyValues.join(", ");
      const allValues = currentLocation 
        ? `Current location: ${currentLocation}\n${details}`
        : details;
      
      onSubmit(allValues);
    } else if (isAgencyScan) {
      // For agency scan, combine all inputs in order (only non-empty values)
      const values = [value, value2, value3, value4, value5].slice(0, inputCount);
      const nonEmptyValues = values.filter(v => v.trim());
      
      // Check if at least one value is provided
      if (nonEmptyValues.length === 0) {
        setError(`${label} is required`);
        return;
      }
      
      // Combine scan level, current location, and input values
      const details = nonEmptyValues.join(", ");
      const parts: string[] = [];
      
      if (scanLevel !== null && scanLevel !== undefined) {
        parts.push(`Scan level: ${scanLevel}`);
      }
      if (currentLocation) {
        parts.push(`Current location: ${currentLocation}`);
      }
      parts.push(details);
      
      const allValues = parts.join("\n");
      onSubmit(allValues);
    } else {
      if (!value.trim()) {
        setError(`${label} is required`);
        return;
      }
      
      // For single input, add current location or scan level if available
      const details = value.trim();
      let finalValue = details;
      
      if (currentLocation && (isSpaceshipMove || isSpaceshipScan)) {
        finalValue = `Current location: ${currentLocation}\n${details}`;
      } else if (isAgencyScan) {
        const parts: string[] = [];
        if (scanLevel !== null && scanLevel !== undefined) {
          parts.push(`Scan level: ${scanLevel}`);
        }
        if (currentLocation) {
          parts.push(`Current location: ${currentLocation}`);
        }
        parts.push(details);
        finalValue = parts.join("\n");
      }
      
      onSubmit(finalValue);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {isSpaceshipMove && currentLocation && (
        <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#e7f3ff", borderRadius: "4px", fontSize: "0.875rem" }}>
          <strong>Note:</strong> The route must start from the current spaceship location (<strong>{currentLocation}</strong>). For collaboration, all collaborating spaceships must start their movement from the same square.
        </div>
      )}
      {isAgencyScan && (
        <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#e7f3ff", borderRadius: "4px", fontSize: "0.875rem" }}>
          <strong>Note:</strong> One of the scanned square(s) must be in direct contact with the current spaceship location{currentLocation ? <> (<strong>{currentLocation}</strong>)</> : ''}.
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#212529" }}>
          {label}
        </label>
        {isAgencyScan && inputCount > 1 ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "nowrap" }}>
            {Array.from({ length: inputCount }).map((_, index) => {
              const inputValue = index === 0 ? value : index === 1 ? value2 : index === 2 ? value3 : index === 3 ? value4 : value5;
              const setInputValue = index === 0 ? setValue : index === 1 ? setValue2 : index === 2 ? setValue3 : index === 3 ? setValue4 : setValue5;
              return (
                <React.Fragment key={index}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setError("");
                    }}
                    placeholder={`Square ${index + 1}`}
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      padding: "0.5rem",
                      fontSize: "0.9rem",
                      border: error ? "1px solid #dc3545" : "1px solid #ced4da",
                      borderRadius: "4px",
                      boxSizing: "border-box"
                    }}
                  />
                  {index < inputCount - 1 && (
                    <span style={{ color: "#6c757d", fontSize: "0.9rem", flexShrink: 0 }}>,</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : isSpaceshipMove && isCollaborating && inputCount > 1 ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "nowrap" }}>
            {currentLocation && (
              <>
                <span style={{ color: "#212529", fontSize: "0.9rem", fontWeight: "500", flexShrink: 0 }}>
                  Current location - {currentLocation}
                </span>
                <span style={{ color: "#6c757d", fontSize: "0.9rem", flexShrink: 0 }}>→</span>
              </>
            )}
            {Array.from({ length: inputCount }).map((_, index) => {
              const inputValue = index === 0 ? value : index === 1 ? value2 : index === 2 ? value3 : index === 3 ? value4 : value5;
              const setInputValue = index === 0 ? setValue : index === 1 ? setValue2 : index === 2 ? setValue3 : index === 3 ? setValue4 : setValue5;
              return (
                <React.Fragment key={index}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setError("");
                    }}
                    placeholder={`Square ${index + 1}`}
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      padding: "0.5rem",
                      fontSize: "0.9rem",
                      border: error ? "1px solid #dc3545" : "1px solid #ced4da",
                      borderRadius: "4px",
                      boxSizing: "border-box"
                    }}
                  />
                  {index < inputCount - 1 && (
                    <span style={{ color: "#6c757d", fontSize: "0.9rem", flexShrink: 0 }}>→</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : isSpaceshipScan && isCollaborating && inputCount > 1 ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "nowrap" }}>
            {Array.from({ length: inputCount }).map((_, index) => {
              const inputValue = index === 0 ? value : index === 1 ? value2 : index === 2 ? value3 : index === 3 ? value4 : value5;
              const setInputValue = index === 0 ? setValue : index === 1 ? setValue2 : index === 2 ? setValue3 : index === 3 ? setValue4 : setValue5;
              return (
                <React.Fragment key={index}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setError("");
                    }}
                    placeholder={`Square ${index + 1}`}
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      padding: "0.5rem",
                      fontSize: "0.9rem",
                      border: error ? "1px solid #dc3545" : "1px solid #ced4da",
                      borderRadius: "4px",
                      boxSizing: "border-box"
                    }}
                  />
                  {index < inputCount - 1 && (
                    <span style={{ color: "#6c757d", fontSize: "0.9rem", flexShrink: 0 }}>,</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : isSpaceshipMove && currentLocation ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "nowrap" }}>
            <span style={{ color: "#212529", fontSize: "0.9rem", fontWeight: "500", flexShrink: 0 }}>
              Current location - {currentLocation}
            </span>
            <span style={{ color: "#6c757d", fontSize: "0.9rem", flexShrink: 0 }}>→</span>
            <input
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError("");
              }}
              placeholder="Square 1"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                padding: "0.5rem",
                fontSize: "0.9rem",
                border: error ? "1px solid #dc3545" : "1px solid #ced4da",
                borderRadius: "4px",
                boxSizing: "border-box"
              }}
            />
          </div>
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError("");
            }}
            placeholder={isSpaceshipScan ? "Square" : (isAgencyScan ? "Square 1" : placeholder)}
            style={{
              width: (isSpaceshipScan || isSpaceshipMove || isAgencyScan) ? "50%" : "100%",
              padding: "0.75rem",
              fontSize: "1rem",
              border: error ? "1px solid #dc3545" : "1px solid #ced4da",
              borderRadius: "4px",
              boxSizing: "border-box"
            }}
          />
        )}
        {error && (
          <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
            {error}
          </div>
        )}
      </div>

      {showCollaborateToggle && !isSpaceshipScan && (
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ fontWeight: 600, color: "#212529", marginBottom: "0.5rem" }}>Collaborate</div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
              <input
                type="radio"
                name="collaborate"
                value="yes"
                checked={isCollaborating}
                onChange={() => onToggleCollaborate?.(true)}
              />
              Yes
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
              <input
                type="radio"
                name="collaborate"
                value="no"
                checked={!isCollaborating}
                onChange={() => onToggleCollaborate?.(false)}
              />
              No
            </label>
          </div>
        </div>
      )}

      {showCollaboratorSelect && (
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ fontWeight: 600, color: "#212529", marginBottom: "0.5rem" }}>Collaborators</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", maxHeight: "200px", overflowY: "auto", border: "1px solid #ced4da", borderRadius: "4px", padding: "0.75rem" }}>
            {collaboratorOptions.map(option => (
              <label key={option.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  value={option.id}
                  checked={selectedCollaborators.includes(option.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onCollaboratorsChange?.([...selectedCollaborators, option.id]);
                    } else {
                      onCollaboratorsChange?.(selectedCollaborators.filter(id => id !== option.id));
                    }
                  }}
                />
                {option.name}
              </label>
            ))}
            {collaboratorOptions.length === 0 && (
              <div style={{ color: "#6c757d" }}>No collaborators available</div>
            )}
          </div>
        </div>
      )}

      {showMovementRules && (
        <div style={{ 
          marginTop: "1rem", 
          marginBottom: "1rem", 
          padding: "0.75rem", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "4px",
          fontSize: "0.875rem",
          color: "#495057",
          whiteSpace: "pre-line"
        }}>
          By yourself - Move 1 square{"\n"}Collaboration of 2 🚀 - Move 2 squares{"\n"}Collaboration of 3 🚀 - Move 3 squares
        </div>
      )}

      {showScanRules && (
        <div style={{ 
          marginTop: "1rem", 
          marginBottom: "1rem", 
          padding: "0.75rem", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "4px",
          fontSize: "0.875rem",
          color: "#495057",
          whiteSpace: "pre-line"
        }}>
          By yourself - Scan 1 square{"\n"}Collaboration of 2 🚀 - Scan 3 squares{"\n"}Collaboration of 3 🚀 - Scan 5 squares
        </div>
      )}

      {showAgencyScanRules && (
        <div style={{ 
          marginTop: "1rem", 
          marginBottom: "1rem", 
          padding: "0.75rem", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "4px",
          fontSize: "0.875rem",
          color: "#495057",
          whiteSpace: "pre-line"
        }}>
          Scan level 1 - Scan 1 square{"\n"}Scan level 2 - Scan 2 squares{"\n"}Scan level 3 - Scan 3 squares
          {scanLevel !== null && scanLevel !== undefined && (
            <div style={{ marginTop: "0.5rem", fontWeight: "600", color: "#212529" }}>
              Current Scan Level: <strong>{scanLevel}</strong>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: "pointer",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px"
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            backgroundColor: isSubmitting ? "#6c757d" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            opacity: isSubmitting ? 0.6 : 1
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
}

